import {useTranslation} from "react-i18next";
import {Button} from "@mantine/core";
import {dibAppApi} from "../../dibAppApi/dibAppApi";
import {useReportAccess} from "./AccessContext";
import {ResourceType} from "./types";
import {DocumentGeneratorBody} from "./docgen/DocumentGeneratorBody";
import {GenerateButtons} from "./generate/GenerateButtons";
import {RelatedContentLEGACY} from "./legacy/RelatedContentLEGACY";
import {SupplierInfoLEGACY} from "./legacy/SupplierInfoLEGACY";
import {renderNode} from "./legacy/renderNodeLegacy";
import {useContentTitle} from "./useContentTitle";
import classes from "./DocumentGeneratorResource.module.css";

export const DocumentGeneratorResource = ({
    resourceId,
}: {
    resourceId: string;
    // isPreview is accepted from the route switch, but getDocgenDocument has no
    // preview-specific request shape today — wire it in if /preview/:topicId
    // for a docgen topic ever needs a different payload.
    isPreview: boolean;
}) => {
    const {t} = useTranslation();

    // RTK Query dedupes against the identical call inside DocumentGeneratorBody
    // (both pass collectionId undefined) — one network request, one cache entry.
    const {data: docData, isFetching} =
        dibAppApi.endpoints.getDocgenDocument.useQuery(
            {resourceId, collectionId: undefined},
            {refetchOnMountOrArgChange: false} // standalone: don't force refetch
        );

    const topicData = docData?.topicData;

    useContentTitle(topicData?.name);

    useReportAccess(resourceId, topicData?.access);

    // The body owns the reset (two mutations, then a fresh generator); this
    // slot only renders the button.
    const topOfForm = ({reset}: {reset: () => void}) => (
        <Button variant="subtle" color="dark" size="compact-sm" onClick={reset}>
            {t("Action.ResetFields", {defaultValue: "Reset fields"})}
        </Button>
    );

    // Title + description are one unit: the desccontainer div is always
    // rendered (same pattern as the HTML article and checklist) so the
    // shared divider under the title (contentBase.module.css) shows even
    // for topics without a description — :empty then drops the padding
    // above the line. Descroot containers are unwrapped so their own
    // desccontainer divs don't nest extra bordered boxes.
    const topOfPreview = topicData ? (
        <>
            <h1>{topicData.name}</h1>
            <div className="desccontainer">
                {topicData.descroot?.flatMap((entry) => {
                    const el = topicData.elements[entry.id];
                    if (!el?.children) return [];
                    return el.children
                        .filter((c): c is {id: string} => "id" in c)
                        .map((c) => renderNode(topicData, c.id, {}, c.id));
                })}
            </div>
        </>
    ) : null;

    const bottomOfForm = topicData ? (
        <GenerateButtons
            isStandalone
            isFetching={isFetching}
            topicMeta={{
                topicId: topicData.topicId,
                resourceId: topicData.id,
                resourceTypeId: ResourceType.documentGenerator,
                name: topicData.name,
            }}
            documents={[
                {
                    id: topicData.id,
                    name: topicData.name,
                    selected: true,
                    resourceTypeId: ResourceType.documentGenerator,
                },
            ]}
        />
    ) : null;

    return (
        <div className={classes.page}>
            <main className={classes.main}>
                <DocumentGeneratorBody
                    documentId={resourceId}
                    isDraft={false}
                    resizableForm={false}
                    topOfForm={topOfForm}
                    topOfPreview={topOfPreview}
                    bottomOfForm={bottomOfForm}
                />
            </main>
            <aside className={classes.aside}>
                {topicData && (
                    <>
                        <SupplierInfoLEGACY document={topicData} />
                        <RelatedContentLEGACY document={topicData} />
                    </>
                )}
            </aside>
        </div>
    );
};
